import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/AdminReviewsPage.css';
import SpotlightCard from '../components/SpotlightCard';
import {
  getAllReviewsUnfiltered,
  updateReview,
  deleteReview,
  Review as DynamoDBReview
} from '../services/dynamoDBService';
import { SentimentType } from '../services/sentimentService';

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

debugLog('[DEBUG AdminReviewsPage] Component loaded');

// Define sort options
enum SortOption {
  RATING_DESC = 'rating_desc',
  RATING_ASC = 'rating_asc',
  DATE_DESC = 'date_desc',
  DATE_ASC = 'date_asc',
  SENTIMENT_POSITIVE = 'sentiment_positive',
  SENTIMENT_NEGATIVE = 'sentiment_negative'
}

// Use the Review interface from the DynamoDB service
type Review = DynamoDBReview;

const AdminReviewsPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [sortedReviews, setSortedReviews] = useState<Review[]>([]);
  const [sortOption, setSortOption] = useState<SortOption>(SortOption.RATING_DESC);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [selectedSentiment, setSelectedSentiment] = useState<SentimentType | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deletingReview, setDeletingReview] = useState<Review | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [activeSentimentFilter, setActiveSentimentFilter] = useState<SentimentType | null>(null);

  debugLog('[DEBUG AdminReviewsPage] Render state:', { loading, reviewCount: reviews.length });

  // Fetch all reviews without filtering when component mounts
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const reviewsData = await getAllReviewsUnfiltered();
        setReviews(reviewsData);
        setSortedReviews(filterAndSortReviews(reviewsData, sortOption, activeSentimentFilter));
        setError(null);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError('Failed to load reviews. Using sample data instead.');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Function to filter reviews by sentiment and sort them
  const filterAndSortReviews = (reviewsToSort: Review[], option: SortOption, sentimentFilter: SentimentType | null): Review[] => {
    // First filter by sentiment if a filter is active
    let filtered = [...reviewsToSort];
    if (sentimentFilter) {
      filtered = filtered.filter(review => review.sentiment === sentimentFilter);
    }
    
    // Then sort the filtered reviews
    switch (option) {
      case SortOption.RATING_DESC:
        return filtered.sort((a, b) => b.rating - a.rating);
      
      case SortOption.RATING_ASC:
        return filtered.sort((a, b) => a.rating - b.rating);
      
      case SortOption.DATE_DESC:
        return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      case SortOption.DATE_ASC:
        return filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      
      case SortOption.SENTIMENT_POSITIVE:
        return filtered.sort((a, b) => {
          // First prioritize positive sentiment
          if (a.sentiment === SentimentType.POSITIVE && b.sentiment !== SentimentType.POSITIVE) return -1;
          if (b.sentiment === SentimentType.POSITIVE && a.sentiment !== SentimentType.POSITIVE) return 1;
          
          // Then neutral
          if (a.sentiment === SentimentType.NEUTRAL && b.sentiment !== SentimentType.NEUTRAL) return -1;
          if (b.sentiment === SentimentType.NEUTRAL && a.sentiment !== SentimentType.NEUTRAL) return 1;
          
          // Then mixed
          if (a.sentiment === SentimentType.MIXED && b.sentiment !== SentimentType.MIXED) return -1;
          if (b.sentiment === SentimentType.MIXED && a.sentiment !== SentimentType.MIXED) return 1;
          
          // If sentiments are the same, sort by rating (highest first)
          return b.rating - a.rating;
        });
      
      case SortOption.SENTIMENT_NEGATIVE:
        return filtered.sort((a, b) => {
          // First prioritize negative sentiment
          if (a.sentiment === SentimentType.NEGATIVE && b.sentiment !== SentimentType.NEGATIVE) return -1;
          if (b.sentiment === SentimentType.NEGATIVE && a.sentiment !== SentimentType.NEGATIVE) return 1;
          
          // Then mixed
          if (a.sentiment === SentimentType.MIXED && b.sentiment !== SentimentType.MIXED) return -1;
          if (b.sentiment === SentimentType.MIXED && a.sentiment !== SentimentType.MIXED) return 1;
          
          // Then neutral
          if (a.sentiment === SentimentType.NEUTRAL && b.sentiment !== SentimentType.NEUTRAL) return -1;
          if (b.sentiment === SentimentType.NEUTRAL && a.sentiment !== SentimentType.NEUTRAL) return 1;
          
          // If sentiments are the same, sort by rating (lowest first)
          return a.rating - b.rating;
        });
      
      default:
        return filtered;
    }
  };

  // Handle sort option change
  const handleSortChange = (option: SortOption) => {
    setSortOption(option);
    setSortedReviews(filterAndSortReviews(reviews, option, activeSentimentFilter));
  };
  
  // Handle sentiment filter click
  const handleSentimentFilterClick = (sentiment: SentimentType) => {
    // If the same sentiment is clicked again, clear the filter
    if (activeSentimentFilter === sentiment) {
      setActiveSentimentFilter(null);
      setSortedReviews(filterAndSortReviews(reviews, sortOption, null));
    } else {
      setActiveSentimentFilter(sentiment);
      setSortedReviews(filterAndSortReviews(reviews, sortOption, sentiment));
    }
  };

  // Function to render stars based on rating
  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} style={{ color: i <= rating ? '#f39c12' : '#ccc' }}>
          ★
        </span>
      );
    }
    return stars;
  };

  // Function to get initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  // Function to get sentiment badge color
  const getSentimentBadgeColor = (sentiment?: string, isPositive?: boolean) => {
    if (sentiment === 'POSITIVE' || isPositive === true) {
      return '#27ae60'; // Green for positive
    } else if (sentiment === 'NEGATIVE' || isPositive === false) {
      return '#e74c3c'; // Red for negative
    } else {
      return '#f39c12'; // Orange for mixed/neutral
    }
  };

  // Function to handle logout
  const handleLogout = async () => {
    debugLog('[DEBUG AdminReviewsPage] Logging out...');
    try {
      await logout();
      debugLog('[DEBUG AdminReviewsPage] Logout successful, navigating to login');
      navigate('/admin');
    } catch (error) {
      console.error('[DEBUG AdminReviewsPage] Logout error:', error);
      // Navigate anyway on error
      navigate('/admin');
    }
  };

  // Function to open the edit sentiment modal
  const handleEditSentiment = (review: Review) => {
    setEditingReview(review);
    setSelectedSentiment(review.sentiment as SentimentType || SentimentType.NEUTRAL);
    setUpdateSuccess(false);
    setUpdateError(null);
  };

  // Function to close the edit sentiment modal
  const handleCloseModal = () => {
    setEditingReview(null);
    setSelectedSentiment(null);
    setUpdateSuccess(false);
    setUpdateError(null);
  };

  // Function to open the delete confirmation modal
  const handleDeleteClick = (review: Review) => {
    setDeletingReview(review);
    setDeleteSuccess(false);
    setDeleteError(null);
  };

  // Function to close the delete confirmation modal
  const handleCloseDeleteModal = () => {
    setDeletingReview(null);
    setDeleteSuccess(false);
    setDeleteError(null);
  };

  // Function to delete the review
  const handleDeleteReview = async () => {
    if (!deletingReview) return;

    try {
      await deleteReview(deletingReview.id);
      
      // Update both reviews lists by removing the deleted review
      const updatedReviews = reviews.filter(review => review.id !== deletingReview.id);
      setReviews(updatedReviews);
      setSortedReviews(filterAndSortReviews(updatedReviews, sortOption, activeSentimentFilter));
      
      setDeleteSuccess(true);
      setDeleteError(null);

      // Close the modal after a delay
      setTimeout(() => {
        handleCloseDeleteModal();
      }, 2000);
    } catch (err) {
      console.error('Error deleting review:', err);
      setDeleteError('An error occurred while deleting the review. Please try again.');
    }
  };

  // Function to update the sentiment
  const handleUpdateSentiment = async () => {
    if (!editingReview || !selectedSentiment) return;

    try {
      // Determine if the sentiment is positive
      const isPositive = selectedSentiment === SentimentType.POSITIVE;

      // Update the review with the new sentiment
      const updatedReview = await updateReview(editingReview.id, {
        sentiment: selectedSentiment,
        isPositive
      });

      if (updatedReview) {
        // Update both reviews lists
        const updatedReviews = reviews.map(review =>
          review.id === updatedReview.id ? updatedReview : review
        );
        setReviews(updatedReviews);
        setSortedReviews(filterAndSortReviews(updatedReviews, sortOption, activeSentimentFilter));
        setUpdateSuccess(true);
        setUpdateError(null);

        // Close the modal after a delay
        setTimeout(() => {
          handleCloseModal();
        }, 2000);
      } else {
        setUpdateError('Failed to update review sentiment. Please try again.');
      }
    } catch (err) {
      console.error('Error updating review sentiment:', err);
      setUpdateError('An error occurred while updating the sentiment. Please try again.');
    }
  };

  return (
    <div className="admin-reviews-page">
      <div className="container">
        <header className="admin-header">
          {/* Admin Navigation */}
          <nav className="admin-nav">
            <NavLink
              to="/admin/reviews"
              className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
            >
              Reviews
            </NavLink>
            <NavLink
              to="/admin/photos"
              className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
            >
              Photos
            </NavLink>
          </nav>

          <h1>Admin Reviews Dashboard</h1>
          <p>View all customer reviews, including those with negative sentiment or low ratings.</p>
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        </header>

        {loading ? (
          <div className="loading-message">Loading reviews...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : reviews.length === 0 ? (
          <div className="no-reviews-message">No reviews found in the database.</div>
        ) : (
          <div className="admin-reviews-container">
            <div className="sort-controls">
              <label htmlFor="sort-select">Sort Reviews By:</label>
              <select
                id="sort-select"
                value={sortOption}
                onChange={(e) => handleSortChange(e.target.value as SortOption)}
                className="sort-select"
              >
                <option value={SortOption.RATING_DESC}>Rating (Highest First)</option>
                <option value={SortOption.RATING_ASC}>Rating (Lowest First)</option>
                <option value={SortOption.DATE_DESC}>Date (Newest First)</option>
                <option value={SortOption.DATE_ASC}>Date (Oldest First)</option>
                <option value={SortOption.SENTIMENT_POSITIVE}>Sentiment (Positive First)</option>
                <option value={SortOption.SENTIMENT_NEGATIVE}>Sentiment (Negative First)</option>
              </select>
            </div>
            <div className="reviews-count">
              <div className="reviews-stats">
                <div className="stats-total">
                  <span>Total Reviews: {reviews.length}</span>
                </div>
                <div className="stats-sentiment">
                  <div className="sentiment-stat positive">
                    <span
                      className={`sentiment-badge-small positive ${activeSentimentFilter === SentimentType.POSITIVE ? 'active' : ''}`}
                      onClick={() => handleSentimentFilterClick(SentimentType.POSITIVE)}
                      title={activeSentimentFilter === SentimentType.POSITIVE ? "Clear filter" : "Filter by positive sentiment"}
                    >
                      POSITIVE
                      {activeSentimentFilter === SentimentType.POSITIVE && <span className="filter-indicator">✓</span>}
                    </span>
                    <span>{reviews.filter(review => review.sentiment === SentimentType.POSITIVE).length}</span>
                  </div>
                  <div className="sentiment-stat negative">
                    <span
                      className={`sentiment-badge-small negative ${activeSentimentFilter === SentimentType.NEGATIVE ? 'active' : ''}`}
                      onClick={() => handleSentimentFilterClick(SentimentType.NEGATIVE)}
                      title={activeSentimentFilter === SentimentType.NEGATIVE ? "Clear filter" : "Filter by negative sentiment"}
                    >
                      NEGATIVE
                      {activeSentimentFilter === SentimentType.NEGATIVE && <span className="filter-indicator">✓</span>}
                    </span>
                    <span>{reviews.filter(review => review.sentiment === SentimentType.NEGATIVE).length}</span>
                  </div>
                  <div className="sentiment-stat neutral">
                    <span
                      className={`sentiment-badge-small neutral ${activeSentimentFilter === SentimentType.NEUTRAL ? 'active' : ''}`}
                      onClick={() => handleSentimentFilterClick(SentimentType.NEUTRAL)}
                      title={activeSentimentFilter === SentimentType.NEUTRAL ? "Clear filter" : "Filter by neutral sentiment"}
                    >
                      NEUTRAL
                      {activeSentimentFilter === SentimentType.NEUTRAL && <span className="filter-indicator">✓</span>}
                    </span>
                    <span>{reviews.filter(review => review.sentiment === SentimentType.NEUTRAL).length}</span>
                  </div>
                  <div className="sentiment-stat mixed">
                    <span
                      className={`sentiment-badge-small mixed ${activeSentimentFilter === SentimentType.MIXED ? 'active' : ''}`}
                      onClick={() => handleSentimentFilterClick(SentimentType.MIXED)}
                      title={activeSentimentFilter === SentimentType.MIXED ? "Clear filter" : "Filter by mixed sentiment"}
                    >
                      MIXED
                      {activeSentimentFilter === SentimentType.MIXED && <span className="filter-indicator">✓</span>}
                    </span>
                    <span>{reviews.filter(review => review.sentiment === SentimentType.MIXED).length}</span>
                  </div>
                </div>
                <div className="stats-rating">
                  <span>
                    Average Rating: {
                      activeSentimentFilter
                        ? (sortedReviews.reduce((sum, review) => sum + review.rating, 0) / sortedReviews.length || 0).toFixed(1)
                        : (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
                    } / 5
                    {activeSentimentFilter && <span className="filtered-indicator"> (filtered)</span>}
                  </span>
                  <div className="average-stars">
                    {renderStars(Math.round(
                      activeSentimentFilter
                        ? (sortedReviews.reduce((sum, review) => sum + review.rating, 0) / sortedReviews.length || 0)
                        : (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length)
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {activeSentimentFilter && (
              <div className="filter-active-indicator">
                <span>
                  Filtering by <strong>{activeSentimentFilter}</strong> sentiment
                </span>
                <button
                  className="clear-filter-button"
                  onClick={() => {
                    setActiveSentimentFilter(null);
                    setSortedReviews(filterAndSortReviews(reviews, sortOption, null));
                  }}
                >
                  Clear Filter
                </button>
              </div>
            )}
            {sortedReviews.map((review) => (
              <SpotlightCard
                key={review.id}
                className="review-card"
                spotlightColor="rgba(230, 126, 34, 0.2)"
              >
                <div className="review-header">
                  <div className="review-avatar">
                    {getInitials(review.name)}
                  </div>
                  <div className="review-info">
                    <h3>{review.name}</h3>
                    <div className="review-date">{review.date}</div>
                    <div className="review-rating">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  <div
                    className="sentiment-badge"
                    style={{ backgroundColor: getSentimentBadgeColor(review.sentiment, review.isPositive) }}
                  >
                    {review.sentiment || (review.isPositive ? 'POSITIVE' : 'NEGATIVE')}
                  </div>
                </div>
                <div className="review-content">
                  {review.content}
                </div>
                <div className="review-actions">
                  <button
                    className="edit-sentiment-button"
                    onClick={() => handleEditSentiment(review)}
                    title="Edit Sentiment"
                    aria-label="Edit Sentiment"
                  >
                    <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16.5 3.5L20.5 7.5L7.5 20.5H3.5V16.5L16.5 3.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    className="delete-review-button"
                    onClick={() => handleDeleteClick(review)}
                    title="Delete Review"
                    aria-label="Delete Review"
                  >
                    <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </SpotlightCard>
            ))}
          </div>
        )}

        {/* Edit Sentiment Modal */}
        {editingReview && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Edit Sentiment</h2>
              <p>Review by: {editingReview.name}</p>
              <p className="modal-review-content">{editingReview.content}</p>
              
              <div className="sentiment-options">
                <div className="sentiment-option-label">Select Sentiment:</div>
                <div className="sentiment-radio-group">
                  <label className="sentiment-radio">
                    <input
                      type="radio"
                      name="sentiment"
                      value={SentimentType.POSITIVE}
                      checked={selectedSentiment === SentimentType.POSITIVE}
                      onChange={() => setSelectedSentiment(SentimentType.POSITIVE)}
                    />
                    <span className="sentiment-label positive">POSITIVE</span>
                  </label>
                  <label className="sentiment-radio">
                    <input
                      type="radio"
                      name="sentiment"
                      value={SentimentType.NEGATIVE}
                      checked={selectedSentiment === SentimentType.NEGATIVE}
                      onChange={() => setSelectedSentiment(SentimentType.NEGATIVE)}
                    />
                    <span className="sentiment-label negative">NEGATIVE</span>
                  </label>
                  <label className="sentiment-radio">
                    <input
                      type="radio"
                      name="sentiment"
                      value={SentimentType.NEUTRAL}
                      checked={selectedSentiment === SentimentType.NEUTRAL}
                      onChange={() => setSelectedSentiment(SentimentType.NEUTRAL)}
                    />
                    <span className="sentiment-label neutral">NEUTRAL</span>
                  </label>
                  <label className="sentiment-radio">
                    <input
                      type="radio"
                      name="sentiment"
                      value={SentimentType.MIXED}
                      checked={selectedSentiment === SentimentType.MIXED}
                      onChange={() => setSelectedSentiment(SentimentType.MIXED)}
                    />
                    <span className="sentiment-label mixed">MIXED</span>
                  </label>
                </div>
              </div>
              
              {updateSuccess && (
                <div className="update-success">Sentiment updated successfully!</div>
              )}
              
              {updateError && (
                <div className="update-error">{updateError}</div>
              )}
              
              <div className="modal-buttons">
                <button className="cancel-button" onClick={handleCloseModal}>Cancel</button>
                <button className="update-button" onClick={handleUpdateSentiment}>Update Sentiment</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingReview && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Delete Review</h2>
              <p>Are you sure you want to delete this review by {deletingReview.name}?</p>
              <p className="modal-review-content">{deletingReview.content}</p>
              
              <div className="delete-warning">
                <p>This action cannot be undone. The review will be permanently removed from the database.</p>
              </div>
              
              {deleteSuccess && (
                <div className="update-success">
                  <h3>Review Deleted!</h3>
                  <p>The review has been successfully removed.</p>
                </div>
              )}
              
              {deleteError && (
                <div className="update-error">
                  <h3>Error</h3>
                  <p>{deleteError}</p>
                </div>
              )}
              
              <div className="modal-buttons">
                <button className="cancel-button" onClick={handleCloseDeleteModal}>Cancel</button>
                <button className="delete-button" onClick={handleDeleteReview}>Delete Review</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviewsPage;